import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { TrackLocations } from "@/hooks/use-segment";
import { useGetStripePortalUrl } from "@/queries/organization-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { ExternalLinkIcon } from "lucide-react";
import { type FC } from "react";

type PaymentMethodRequiredModalProps = {
    open: boolean;
    onClose: () => void;
};

export const PaymentMethodRequiredModal: FC<PaymentMethodRequiredModalProps> = ({ open, onClose }) => {
    const { mutate: getStripePortalUrl, isPending } = useGetStripePortalUrl();
    const { toast } = useToast();

    const handleOpenStripePortal = () => {
        getStripePortalUrl(undefined, {
            onSuccess: (data) => {
                if (data.portalUrl) {
                    window.open(data.portalUrl, "_blank");
                }
                onClose();
            },
            onError: (error) => {
                toast({
                    title: "Failed to open billing portal",
                    description: formatError(error),
                });
                onClose();
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md" data-track-location={TrackLocations.PaymentMethodRequiredModal}>
                <DialogHeader className="mb-1.5">
                    <DialogTitle>Payment Method Required</DialogTitle>
                </DialogHeader>
                <DialogDescription>
                    To upgrade to Core, you need to set up a default payment method first. You can do this through the
                    Stripe billing portal.
                </DialogDescription>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button
                        variant="primary"
                        onClick={handleOpenStripePortal}
                        disabled={isPending}
                        className="flex items-center gap-2"
                        data-tracking-id="open-billing-portal-payment-method-required-modal"
                    >
                        <span>{isPending ? "Loading..." : "Open Billing Portal"}</span>
                        {!isPending && <ExternalLinkIcon className="size-4" />}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
